import { Component, OnInit, Output, EventEmitter } from '@angular/core';

import { TextFieldComponent } from './text-field.component';
import {AbstractControl, ValidatorFn, Validators} from '@angular/forms';

@Component({
  selector: 'app-timecode-field-component',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss']
})
export class TimecodeFieldComponent extends TextFieldComponent {
  @Output() timecodeChanged = new EventEmitter<{field: string, value: string}>();

  frames: boolean;
  dropFrame: boolean = false;
  fps: number;
  fullPattern: string = "^\d{2}[:;]\d{2}[:;]\d{2}$";
  minTimecode: string;
  pattern: string = "^((((((((((\\d)?\\d?)[:;]?)\\d)?\\d)?[:;])?\\d)?\\d)?[:;])?\\d)?\\d$";
  separatorRegex: RegExp;

  ngOnInit() {
    super.ngOnInit();

    this.frames = (this.field.frames !== false);
    this.maxLength = this.frames ? 11 : 8;

    let separatorPattern = "(\\d{2}[:;])?\\d{2}";
    if (this.frames) { separatorPattern = "(" + separatorPattern + "[:;])?\\d{2}" }
    this.separatorRegex = new RegExp("^" + separatorPattern + "$");

    let validators: ValidatorFn[] = [
      Validators.maxLength(this.maxLength),
      Validators.pattern(this.pattern),
      this.validateFPS(),
      this.validateMinTimecode(),
      this.validateDropFrameTimecode()
    ];
    if (this.required === true) {validators.push(Validators.required)}

    this.control.setValidators(validators);
    this.control.setValue(this.padValue(this.value), {emitEvent: false});
  }

  padValue(value) {
    if (value != null) {
      let digits = value.replace(/[^\d]/g, '')
      if (digits.length > 0) {
        let length = this.frames ? 8 : 6;
        let segments = digits.padStart(length,'0').match(/\d{1,2}/g);
        return this.joinSegments(segments);
      }
    }
  }

  getSegments(timecode: string): number[] {
    return timecode.split(/[:;]/).map((s) => +s);
  }

  joinSegments(segments: number[]) {
    if (segments.length != 4) {
      return segments.join(':');
    } else {
      let separator = this.dropFrame ? ';' : ':';
      return '' + segments[0] + ':' + segments[1] + ':' + segments[2] + separator + segments[3];
    }
  }

  onKey(event): boolean {
    if (!super.onKey(event)) {return false};

    let code = event.keyCode
    let element = event.target

    if (code >= 48 && code <= 57) { // 0-9
      let value = this.control.value;
      let length = value != null ? value.length : 0;
      if (element.selectionStart === length) {
        if (this.separatorRegex.test(value)) {
          let separator = this.dropFrame && length > 6 ? ';' : ':';
          this.control.setValue(value+separator)
        }
      }
    }

    return true;
  }

  onBlur(event) {
    this.value = this.padValue(this.value);
    let result = super.onBlur(event);
    if (this.dirty) {
      this.timecodeChanged.emit({field: this.id, value: this.value});
    }
    return result;
  }

  setMinTimecode(timecode: string) {
    this.minTimecode = timecode;
    this.validate();
  }

  setStandard(standard: {fps: number, drop: boolean}) {
    if (standard == null) { return }

    this.fps = standard.fps;
    this.dropFrame = standard.drop;

    if (!this.blank) {
      this.setValue(this.padValue(this.value));
    } else if (this.control != null) {
      // Update validity when fps changes
      this.control.updateValueAndValidity({emitEvent: false});
    }
  }

  convertStandard(standard: {fps: number, drop: boolean, start: string}) {
    if (standard == null || this.value == null || this.fps == null) { return }

    let currentFps = this.fps;
    let currentDrop = this.dropFrame;

    let newFrames;
    if (this.id === "duration") {
      newFrames = this.convertTimecodeToFrames(this.value);

      this.fps = standard.fps;
      this.dropFrame = standard.drop;
    } else {
      let startFrame = this.convertTimecodeToFrames(standard.start);
      let frames = this.convertTimecodeToFrames(this.value);

      let framesFromStart = frames-startFrame;

      this.fps = standard.fps;
      this.dropFrame = standard.drop;

      if (framesFromStart < 0) {
        newFrames = null
      } else {
        let newStartFrame = this.convertTimecodeToFrames(standard.start);
        newFrames = newStartFrame+framesFromStart;
      }
    }

    let newTimecode = newFrames != null ? this.convertFramesToTimecode(newFrames) : '';
    this.setValue(newTimecode, false);
  }

  offsetBy(event: {offset: string, subtract: boolean, anchor: string, before: boolean}) {
    if (this.value == null || this.fps == null) { return }
    if (this.id === "duration") { return }

    let offset = event.offset;
    let subtract = (event.subtract == true);
    let anchor = event.anchor;
    let before = (event.before == true);

    let anchorFrames = this.convertTimecodeToFrames(anchor);
    let frames = this.convertTimecodeToFrames(this.value);

    if (before) {
      if (frames >= anchorFrames) { return }
    } else {
      if (frames <= anchorFrames) { return }
    }

    let offsetFrames = this.convertTimecodeToFrames(offset);

    let newFrames: number;
    if (subtract) {
      newFrames = frames-offsetFrames;
    } else {
      newFrames = frames+offsetFrames;
    }

    let newTimecode = newFrames > 0 ? this.convertFramesToTimecode(newFrames) : null;

    this.setValue(newTimecode, false);
  }

  lessThan(otherTimecode: string): boolean {
    if (this.value == null || otherTimecode == null) { return false }
    let frames = this.convertTimecodeToFrames(this.value);
    let otherFrames = this.convertTimecodeToFrames(otherTimecode);
    return frames < otherFrames
  }

  greaterThan(otherTimecode: string): boolean {
    if (this.value == null || otherTimecode == null) { return false }
    let frames = this.convertTimecodeToFrames(this.value);
    let otherFrames = this.convertTimecodeToFrames(otherTimecode);
    return frames > otherFrames
  }

  calculateDuration(timecodeIn: string , timecodeOut: string, inclusive: boolean = true) {
    if (timecodeIn == null || timecodeOut == null) {
      this.setValue(null)
    } else {
      let framesIn = this.convertTimecodeToFrames(timecodeIn);
      let framesOut = this.convertTimecodeToFrames(timecodeOut);
      let framesDuration = framesOut - framesIn;
      if (framesDuration < 0) {
        this.setValue(null);
      } else {
        if (inclusive) { framesDuration += 1 }
        this.setValue(this.convertFramesToTimecode(framesDuration));
      }
    }
  }

  calculateTimecodeIn(timecodeOut: string, duration: string, inclusive: boolean = true) {
    if (timecodeOut == null || duration == null) {
      this.setValue(null)
    } else {
      let framesOut = this.convertTimecodeToFrames(timecodeOut);
      let framesDuration = this.convertTimecodeToFrames(duration);
      if (inclusive) { framesDuration -= 1 }
      let framesIn = framesOut - framesDuration;
      if (framesIn < 0) {
        this.setValue(null);
      } else {
        this.setValue(this.convertFramesToTimecode(framesIn));
      }
    }
  }

  calculateTimecodeOut(timecodeIn: string, duration: string, inclusive: boolean = true) {
    if (timecodeIn == null || duration == null) {
      this.setValue(null)
    } else {
      let framesIn = this.convertTimecodeToFrames(timecodeIn);
      let framesDuration = this.convertTimecodeToFrames(duration);
      if (inclusive) { framesDuration -= 1 }
      let framesOut = framesIn + framesDuration;
      if (framesOut < 0) {
        this.setValue(null);
      } else {
        this.setValue(this.convertFramesToTimecode(framesOut));
      }
    }
  }

  convertTimecodeToFrames(timecode: string): number {
    let fps = this.fps != null ? this.fps : 60;
    let fpm = 60*fps;
    let fph = 60*fpm;

    let segments = this.getSegments(timecode);

    let h = segments[0]*fph;
    let m = segments[1]*fpm;
    let s = segments[2]*fps;
    let f = segments[3];

    let frames = h+m+s+f;

    if (this.dropFrame) {
      // In drop-frame we skip frames every minute
      // Except for every 10th minute
      let minutes = (segments[0]*60)+segments[1];
      let tenthMinutes = Math.floor(minutes/10);

      let skipped = 0;
      switch (this.fps) {
      case 30:
        // Skip 2 frames every minute on 29.97 drop
        skipped = 2*minutes - 2*tenthMinutes;
        break;
      case 60:
        // Skip 4 frames every minute on 59.94 drop
        skipped = 4*minutes - 4*tenthMinutes;
        break;
      }

      frames -= skipped;
    }

    if (isNaN(frames)) {
      return 0;
    } else {
      return frames;
    }
  }

  convertFramesToTimecode(frames: number): string {
    let fps = this.fps != null ? this.fps : 60;
    let fpm = 60*fps;
    let fph = 60*fpm;

    if (this.dropFrame) {
      // In drop-frame we skip frames every minute
      // Except for every 10th minute
      let skipCount = 0;
      switch (this.fps) {
      case 30:
        skipCount = 2;
        break;
      case 60:
        skipCount = 4;
        break;
      }

      let minuteIndex = 1;
      let totalFrames = frames;
      while (totalFrames >= fpm) {
        totalFrames -= fpm;
        if (minuteIndex == 10) {
          minuteIndex = 1;
          continue;
        }
        frames += skipCount;
        totalFrames += skipCount;
        minuteIndex += 1;
      }
    }

    let h = Math.floor(frames/fph);
    frames -= h*fph;
    let m = Math.floor(frames/fpm);
    frames -= m*fpm;
    let s = Math.floor(frames/fps);
    frames -= s*fps;
    let f = frames;

    let segments = [h,m,s,f].map((s) => (''+s).padStart(2,'0'))
    return this.joinSegments(segments);
  }

  validateFPS(): ValidatorFn {
    return  (control: AbstractControl): {[key: string]: any} | null => {
      if (this.frames && this.fps != null) {
        let value = control.value != null ? control.value : '';
        let segments = this.getSegments(value);
        let frames = segments[3]
        if (frames != null && frames >= this.fps) {
          return {'fps': this.fps}
        }
      }
      return null;
    }
  }

  validateMinTimecode(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (this.minTimecode != null) {
        let minFrames = this.convertTimecodeToFrames(this.minTimecode);
        let value = control.value != null ? control.value : '';
        let frames = this.convertTimecodeToFrames(value);
        if (frames < minFrames) {
          return {'minTimecode': this.minTimecode};
        }
      }
      return null;
    }
  }

  validateDropFrameTimecode(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (this.frames && this.dropFrame) {
        let value = control.value != null ? control.value : '';
        let segments = this.getSegments(value);
        let minutes = segments[1];
        let seconds = segments[2];
        let frames = segments[3];
        let tenthMinute = ((minutes % 10) == 0)
        if (seconds == 0 && !tenthMinute) {
          // On drop frame timecode, frames are
          // skipped every minute except every tenth
          switch (this.fps) {
          case 30:
            // First 2 frames (0-1) on 29.97 drop are invalid
            if (frames <= 1) {
              return {'dropFrame': this.fps}
            }
            break;
          case 60:
            // First 4 frames (0-3) on 59.94 drop are invalid
            if (frames <= 3) {
              return {'dropFrame': this.fps}
            }
            break;
          }
        }
      }
      return null;
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/forms/timecode-field.component.ts